import { useState, useRef } from "react";
import { Upload, X, CheckCircle2, Loader2, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { useBoard } from "@/context/BoardContext";

const initial = { name: "", email: "", phone: "", service_type: "", city: "", address: "", timeline: "", budget: "", project_details: "" };

export default function QuoteForm({ defaultService = "", extraInspiration = [] }) {
  const { items: boardItems, remove: removeBoard, clear: clearBoard } = useBoard();
  const [form, setForm] = useState({ ...initial, service_type: defaultService });
  const [photos, setPhotos] = useState([]); // {id, name, preview}
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleFiles = async (files) => {
    const list = Array.from(files).slice(0, 6);
    setUploading(true);
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("scope", "lead");
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { id: data.id, name: file.name, preview: URL.createObjectURL(file) }]);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    }
    setUploading(false);
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service_type || !form.project_details) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const inspiration_projects = [...boardItems.map((b) => b.title), ...extraInspiration];
      await api.post("/leads", { ...form, photo_ids: photos.map((p) => p.id), inspiration_projects });
      setDone(true);
      clearBoard();
      toast.success("Quote request sent!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-card border border-border p-10 text-center" data-testid="quote-success">
        <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-bold mb-2">Request Received</h3>
        <p className="text-muted-foreground max-w-md mx-auto">
          Thanks, {form.name.split(" ")[0]}! We got your details and will reach out shortly to schedule your free estimate.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-card border border-border p-6 sm:p-8 space-y-5" data-testid="quote-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">Full Name *</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Jane Homeowner" className="mt-1.5 rounded-sm" data-testid="quote-name" />
        </div>
        <div>
          <Label className="font-semibold">Phone *</Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="(920) 555-0123" className="mt-1.5 rounded-sm" data-testid="quote-phone" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">Email *</Label>
          <Input type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-1.5 rounded-sm" data-testid="quote-email" />
        </div>
        <div>
          <Label className="font-semibold">Service Needed *</Label>
          <Select value={form.service_type} onValueChange={set("service_type")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="quote-service"><SelectValue placeholder="Select a service" /></SelectTrigger>
            <SelectContent>
              {SERVICES.map((s) => <SelectItem key={s.slug} value={s.title} data-testid={`quote-service-${s.slug}`}>{s.title}</SelectItem>)}
              <SelectItem value="Other">Other / Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="font-semibold">City</Label>
          <Input value={form.city} onChange={set("city")} placeholder="Watertown" className="mt-1.5 rounded-sm" data-testid="quote-city" />
        </div>
        <div>
          <Label className="font-semibold">Timeline</Label>
          <Select value={form.timeline} onValueChange={set("timeline")}>
            <SelectTrigger className="mt-1.5 rounded-sm" data-testid="quote-timeline"><SelectValue placeholder="When do you want this done?" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ASAP">ASAP</SelectItem>
              <SelectItem value="1-3 months">1–3 months</SelectItem>
              <SelectItem value="3-6 months">3–6 months</SelectItem>
              <SelectItem value="Just researching">Just researching</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label className="font-semibold">Project Details *</Label>
        <Textarea value={form.project_details} onChange={set("project_details")} placeholder="Tell us about your project — size, current condition, what you're hoping for…" rows={4} className="mt-1.5 rounded-sm" data-testid="quote-details" />
      </div>

      {(boardItems.length > 0 || extraInspiration.length > 0) && (
        <div className="bg-primary/5 border border-primary/20 p-4" data-testid="quote-inspiration">
          <Label className="font-semibold flex items-center gap-2"><Heart className="h-4 w-4 fill-primary text-primary" /> Inspiration from our gallery</Label>
          <p className="text-xs text-muted-foreground mt-1">These projects will be sent with your request so we know the look you're after.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {boardItems.map((b) => (
              <div key={b.id} className="relative group" data-testid={`quote-insp-${b.id}`}>
                <img src={b.image} alt={b.title} className="h-16 w-20 object-cover border border-border" />
                <button type="button" onClick={() => removeBoard(b.id)} className="absolute -top-1.5 -right-1.5 bg-secondary text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
              </div>
            ))}
            {extraInspiration.map((t) => (
              <span key={t} className="text-xs font-semibold bg-card border border-border px-3 py-2 self-center">{t}</span>
            ))}
          </div>
        </div>
      )}

      <div>
        <Label className="font-semibold">Project Photos (optional)</Label>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          className="mt-1.5 border-2 border-dashed border-border bg-muted/40 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer rounded-sm p-8 text-center"
          data-testid="quote-upload-zone"
        >
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="quote-file-input" />
          {uploading ? <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" /> : <Upload className="h-6 w-6 text-muted-foreground mx-auto" />}
          <p className="text-sm text-muted-foreground mt-2">Drag photos here or <span className="text-primary font-semibold">browse</span> — up to 6 images</p>
        </div>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-3">
            {photos.map((p) => (
              <div key={p.id} className="relative aspect-square group" data-testid={`quote-photo-${p.id}`}>
                <img src={p.preview} alt={p.name} className="w-full h-full object-cover rounded-sm" />
                <button type="button" onClick={() => removePhoto(p.id)} className="absolute -top-1.5 -right-1.5 bg-secondary text-white rounded-full p-0.5" data-testid={`remove-photo-${p.id}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Button type="submit" disabled={submitting} className="w-full h-12 bg-primary hover:bg-primary/90 rounded-sm font-bold text-base" data-testid="quote-submit">
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Request My Free Quote"}
      </Button>
      <p className="text-xs text-muted-foreground text-center">No obligation. We typically respond within one business day.</p>
    </form>
  );
}
